"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth-context";
import { authedFetch } from "@/lib/api-fetch";
import { NOTE_ACTION, NOTE_COPY } from "@/lib/note/copy";

/**
 * 노트 상세 하단 — 안내 문구 + ♥ 좋아요(수) + ↗ 공유하기.
 * - 판정·지급은 서버 (/api/note/like · /api/note/share-link). 화면은 결과만 표시.
 * - 비로그인 클릭 → /login?next=/note/{slug} (로그인 후 같은 글로 복귀).
 * - ?s= 공유 링크로 들어오면 /api/note/share-visit 1회 전송 (비로그인 허용).
 */
export default function NoteActions({ slug, title }: { slug: string; title: string }) {
  const { firebaseUser, loading } = useAuth();
  const router = useRouter();
  const [count, setCount] = useState<number | null>(null);
  const [liked, setLiked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareVisitSent = useRef(false);

  const loadLikes = useCallback(async () => {
    try {
      const url = `/api/note/like?postId=${encodeURIComponent(slug)}`;
      const res = firebaseUser ? await authedFetch(url) : await fetch(url);
      if (!res.ok) return;
      const j = (await res.json()) as { count: number; liked: boolean };
      setCount(j.count);
      setLiked(j.liked);
    } catch {
      /* 표시만 생략 */
    }
  }, [slug, firebaseUser]);

  useEffect(() => {
    if (!loading) loadLikes();
  }, [loading, loadLikes]);

  // 공유 링크(?s=) 방문 — 인증 상태가 정해진 뒤 한 번만.
  useEffect(() => {
    if (loading || shareVisitSent.current) return;
    const s = new URLSearchParams(window.location.search).get("s");
    if (!s || !/^[a-f0-9]{12}$/.test(s)) return;
    shareVisitSent.current = true;
    const init: RequestInit = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ shareCode: s }),
    };
    (firebaseUser ? authedFetch("/api/note/share-visit", init) : fetch("/api/note/share-visit", init)).catch(() => {});
  }, [loading, firebaseUser]);

  const goLogin = () => router.push(`/login?next=${encodeURIComponent(`/note/${slug}`)}`);

  const onLike = async () => {
    if (!firebaseUser) return goLogin();
    if (busy) return;
    setBusy(true);
    try {
      const res = await authedFetch("/api/note/like", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postId: slug, action: liked ? "unlike" : "like" }),
      });
      if (res.ok) await loadLikes();
    } finally {
      setBusy(false);
    }
  };

  const onShare = async () => {
    if (!firebaseUser) return goLogin();
    if (busy) return;
    setBusy(true);
    try {
      const res = await authedFetch("/api/note/share-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postId: slug }),
      });
      if (!res.ok) return;
      const { path } = (await res.json()) as { path: string };
      // 공유 링크 유입 구분 (2026-10-12 #47) — utm_source=share · utm_medium=navigator (공유 문구·?s= 코드는 그대로)
      const shareUrl = new URL(path, window.location.origin);
      shareUrl.searchParams.set("utm_source", "share");
      shareUrl.searchParams.set("utm_medium", "navigator");
      const url = shareUrl.toString();
      if (typeof navigator.share === "function") {
        try {
          await navigator.share({ title, url });
          return;
        } catch (e) {
          if (e instanceof DOMException && e.name === "AbortError") return; // 사용자가 공유창을 닫음
        }
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* 공유 실패 — 화면 변화 없음 */
    } finally {
      setBusy(false);
    }
  };

  // 글자 크기는 클래스로 (모바일 16px · 데스크톱 15px).
  const btn = (active: boolean) => ({
    fontWeight: 700,
    padding: "10px 18px",
    borderRadius: 999,
    border: `1px solid ${active ? "var(--color-red)" : "rgba(28,26,25,0.15)"}`,
    background: active ? "#FFF3F1" : "#ffffff",
    color: active ? "var(--color-red-text)" : "var(--color-ink)",
  });

  return (
    <div className="mt-10 text-center" data-testid="note-actions">
      <p className="text-[color:var(--color-ink)]" style={{ fontSize: 15, fontWeight: 700, whiteSpace: "normal" }}>
        {NOTE_COPY.postFooter}
      </p>
      <div className="mt-4 flex items-center justify-center gap-3">
        <button type="button" onClick={onLike} aria-pressed={liked} className="text-[16px] lg:text-[15px]" style={btn(liked)} data-testid="note-like">
          <span aria-hidden>♥</span> {NOTE_ACTION.like}{" "}
          <span style={{ fontFamily: "var(--font-en)" }} data-testid="note-like-count">
            {count === null ? "" : count.toLocaleString("ko-KR")}
          </span>
        </button>
        <button type="button" onClick={onShare} className="text-[16px] lg:text-[15px]" style={btn(false)} data-testid="note-share">
          <span aria-hidden>{copied ? "✓" : "↗"}</span> {NOTE_ACTION.share}
        </button>
      </div>
    </div>
  );
}
